import React, { useState, useEffect, useCallback } from 'react';
import { useSelectedBrand } from '../hooks/useApi';
import LoadingSpinner from '../components/LoadingSpinner';
import apiService from '../services/api';

const Performance = () => {
  const { selectedBrandId, setSelectedBrandId, brands: brandOptions, brandsLoading } = useSelectedBrand();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    if (!selectedBrandId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getAnalyticsInsights(selectedBrandId, days);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBrandId, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await apiService.refreshAnalytics(selectedBrandId);
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  if (brandsLoading) return <LoadingSpinner />;

  const TrendIcon = ({ trend }) => {
    if (trend === 'up') return <i className="fas fa-arrow-up text-green-500"></i>;
    if (trend === 'down') return <i className="fas fa-arrow-down text-red-500"></i>;
    return <i className="fas fa-minus text-gray-400"></i>;
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Performance</h1>
          <p className="text-sm text-gray-500 mt-1">Content analytics and insights</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
          {brandOptions.length > 1 && (
            <select
              value={selectedBrandId || ''}
              onChange={e => setSelectedBrandId(Number(e.target.value))}
              className="border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500"
            >
              {brandOptions.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 bg-accent-600 text-white rounded-xl text-sm font-medium hover:bg-accent-700 disabled:opacity-50 transition-colors"
          >
            {refreshing ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-sync mr-1"></i>}
            Sync
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{error}</div>
      )}

      {loading ? <LoadingSpinner /> : data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="text-sm text-gray-500 mb-1">Total Engagement</div>
              <div className="text-2xl font-bold text-gray-900">{data.total_engagement.toLocaleString()}</div>
              <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                <TrendIcon trend={data.engagement_trend} />
                {data.engagement_trend}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="text-sm text-gray-500 mb-1">Followers</div>
              <div className="text-2xl font-bold text-gray-900">{data.current_followers.toLocaleString()}</div>
              <div className="text-xs text-gray-400 mt-1">
                {data.follower_growth >= 0 ? '+' : ''}{data.follower_growth} in {days}d
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="text-sm text-gray-500 mb-1">Avg Daily Engagement</div>
              <div className="text-2xl font-bold text-gray-900">{data.avg_daily_engagement}</div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="text-sm text-gray-500 mb-1">Posts Analyzed</div>
              <div className="text-2xl font-bold text-gray-900">{data.post_metrics?.length || 0}</div>
            </div>
          </div>

          {/* Insights */}
          {data.insights?.length > 0 && (
            <div className="bg-accent-50 rounded-2xl border border-accent-200 p-6">
              <h3 className="text-sm font-semibold text-accent-800 uppercase tracking-wide mb-3">
                <i className="fas fa-lightbulb mr-2"></i>Insights
              </h3>
              <div className="space-y-2">
                {data.insights.map((insight, i) => (
                  <p key={i} className="text-sm text-accent-700 flex items-start gap-2">
                    <span className="text-accent-400 mt-0.5">-</span>
                    {insight}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* By Topic */}
          {data.topic_summary?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">By Topic</h3>
              <div className="space-y-3">
                {data.topic_summary.map(topic => (
                  <div key={topic.post_type} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-700 w-32">{topic.post_type}</span>
                      <span className="text-xs text-gray-400">{topic.count} posts</span>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-gray-600">
                      <span>
                        <i className="fas fa-eye mr-1 text-gray-400"></i>
                        avg {topic.avg_impressions.toLocaleString()}
                      </span>
                      <span>
                        <i className="fas fa-thumbs-up mr-1 text-gray-400"></i>
                        {topic.total_likes}
                      </span>
                      <span>
                        <i className="fas fa-comment mr-1 text-gray-400"></i>
                        {topic.total_comments}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* By Day of Week */}
          {data.day_summary?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">By Day of Week</h3>
              <div className="grid grid-cols-7 gap-2">
                {data.day_summary.map(day => {
                  const maxImp = Math.max(...data.day_summary.map(d => d.avg_impressions), 1);
                  const pct = Math.round(day.avg_impressions / maxImp * 100);
                  return (
                    <div key={day.day_index} className="text-center">
                      <div className="text-xs text-gray-400 mb-2">{day.day.slice(0, 3)}</div>
                      <div className="h-24 flex items-end justify-center">
                        <div
                          className={`w-8 rounded-t ${day.post_count > 0 ? 'bg-accent-400' : 'bg-gray-200'}`}
                          style={{ height: `${Math.max(pct, 4)}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{day.avg_impressions.toLocaleString()}</div>
                      <div className="text-xs text-gray-400">{day.post_count}p</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top Posts */}
          {data.top_posts?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Top Posts by Impressions</h3>
              <div className="space-y-3">
                {data.top_posts.map((post, i) => (
                  <div key={post.id} className="flex items-center gap-4 py-2 border-b border-gray-100 last:border-0">
                    <span className="text-lg font-bold text-gray-300 w-6 text-center">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">{post.content_preview}</p>
                      <div className="flex gap-3 mt-1 text-xs text-gray-400">
                        {post.post_type && <span>{post.post_type}</span>}
                        <span>{new Date(post.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 flex-shrink-0">
                      <span><i className="fas fa-eye mr-1"></i>{post.impressions.toLocaleString()}</span>
                      <span><i className="fas fa-thumbs-up mr-1"></i>{post.likes}</span>
                      <span><i className="fas fa-comment mr-1"></i>{post.comments}</span>
                      <span className="text-accent-600 font-medium">{post.engagement_rate}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Performance;
